import type { MemberRole } from "@cs2/contracts";

/** Die Kapitel entsprechen den Seiten der AppShell (`active`). */
export type OnboardingChapter = "quickview" | "editor" | "catalog";

export type IllustrationKey =
  | "welcome"
  | "select"
  | "slots"
  | "steps"
  | "utility"
  | "status"
  | "live-follow"
  | "live-lead"
  | "team"
  | "team-manage"
  | "readonly"
  | "tactics"
  | "draw"
  | "route-floors"
  | "utility-place"
  | "library"
  | "inspector"
  | "save"
  | "snapshots"
  | "publish"
  | "rollback"
  | "eligibility"
  | "callouts"
  | "catalog-browse"
  | "catalog-modes"
  | "catalog-filters"
  | "catalog-details"
  | "catalog-create"
  | "catalog-share";

/**
 * Ein Erklärschritt. Reine Daten ohne React, damit auch das E2E-Setup die IDs
 * importieren kann. Ändert sich ein Schritt inhaltlich wesentlich, bekommt er
 * eine neue `id` — der Fortschritt hängt an der ID, nicht an der Position.
 */
export type OnboardingStep = {
  id: string;
  chapter: OnboardingChapter;
  roles: readonly MemberRole[];
  title: string;
  body: string;
  illustration: IllustrationKey;
};

export const CHAPTERS: readonly {
  id: OnboardingChapter;
  title: string;
}[] = [
  { id: "quickview", title: "Quickview" },
  { id: "editor", title: "Taktik-Editor" },
  { id: "catalog", title: "Granatenkatalog" },
];

const everyone: readonly MemberRole[] = ["viewer", "editor", "captain"];
const writers: readonly MemberRole[] = ["editor", "captain"];
const followers: readonly MemberRole[] = ["viewer", "editor"];
const captain: readonly MemberRole[] = ["captain"];

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  // Quickview -------------------------------------------------------------
  {
    id: "qv-welcome",
    chapter: "quickview",
    roles: everyone,
    title: "Willkommen bei CS2 Tactics",
    body: "Oben wechselst du zwischen den Bereichen, daneben siehst du deine Rolle. Über das Kontomenü erreichst du alle Aktionen und meldest dich ab.",
    illustration: "welcome",
  },
  {
    id: "qv-select",
    chapter: "quickview",
    roles: everyone,
    title: "Taktik wählen",
    body: "Wähle Map, Kategorie und Taktik und schalte zwischen T-Seite und CT-Seite um. Deine Auswahl bleibt beim nächsten Besuch erhalten.",
    illustration: "select",
  },
  {
    id: "qv-slot",
    chapter: "quickview",
    roles: everyone,
    title: "Deine Position",
    body: "Die Tabs P1 bis P5 zeigen jede Spielerrolle. Wähle deine Position, um nur deine Route, Aufgabe und Utility zu sehen.",
    illustration: "slots",
  },
  {
    id: "qv-steps",
    chapter: "quickview",
    roles: everyone,
    title: "Schritt für Schritt",
    body: "„Abspielen“ animiert die Route auf der Karte. Ein Klick auf einen Schritt unter „MEINE AUFGABE“ hebt ihn hervor, ein zweiter Klick hebt die Markierung auf.",
    illustration: "steps",
  },
  {
    id: "qv-utility",
    chapter: "quickview",
    roles: everyone,
    title: "Utility-Details",
    body: "Ein Klick auf eine Utility-Karte oder einen Chip öffnet das Detail mit START-, AIM- und RESULT-Bild. So triffst du jede Granate wie geübt.",
    illustration: "utility",
  },
  {
    id: "qv-status",
    chapter: "quickview",
    roles: everyone,
    title: "Aktueller Stand",
    body: "„Veröffentlicht“ zeigt den freigegebenen Stand, „LOKALE VORSCHAU“ den zwischengespeicherten Stand ohne Verbindung. Bei „Neue Version“ gibt es Änderungen; „Aktualisieren“ lädt neu, das Download-Symbol exportiert die Karte als Bild.",
    illustration: "status",
  },
  {
    id: "qv-live-follow",
    chapter: "quickview",
    roles: followers,
    title: "Live folgen",
    body: "Ist der Captain live, folgt deine Ansicht automatisch seiner Taktik-Auswahl. Eine Meldung „DU BIST Px“ sagt dir, welche Position du spielst.",
    illustration: "live-follow",
  },
  {
    id: "qv-live-lead",
    chapter: "quickview",
    roles: captain,
    title: "Live steuern",
    body: "Als Captain steuerst du live, welche Taktik alle sehen, und die Positionen werden automatisch verteilt. Passen mehrere Mitglieder auf einen Slot, ordnest du sie unter „POSITIONEN ZUORDNEN“ per Klick zu.",
    illustration: "live-lead",
  },
  {
    id: "qv-team",
    chapter: "quickview",
    roles: followers,
    title: "Mein Team",
    body: "„Mein Team“ zeigt, wie viele Mitglieder gerade online sind. Ein Klick öffnet die Mitgliederliste mit ihrem Status.",
    illustration: "team",
  },
  {
    id: "qv-team-manage",
    chapter: "quickview",
    roles: captain,
    title: "Team verwalten",
    body: "Unter „Mitglieder“ lädst du neue Mitglieder ein, änderst Rollen und deaktivierst Zugänge. Es gibt genau einen Captain – wer zum Captain wird, löst dich als Captain ab.",
    illustration: "team-manage",
  },

  // Taktik-Editor ---------------------------------------------------------
  {
    id: "ed-readonly",
    chapter: "editor",
    roles: ["viewer"],
    title: "Nur-Lesen-Ansicht",
    body: "Als Viewer kannst du Taktiken im Editor ansehen, aber nicht ändern. Für Bearbeitungsrechte wende dich an deinen Captain.",
    illustration: "readonly",
  },
  {
    id: "ed-tactics",
    chapter: "editor",
    roles: writers,
    title: "Taktiken verwalten",
    body: "„Taktiken verwalten“ öffnet die Liste mit Suche. Dort legst du mit „NEUE TAKTIK“ an, duplizierst oder löschst Taktiken.",
    illustration: "tactics",
  },
  {
    id: "ed-draw",
    chapter: "editor",
    roles: writers,
    title: "Auf der Karte zeichnen",
    body: "Mit „SPIELERROUTE“ setzt jeder Klick einen Routenpunkt und legt einen Schritt an; „PFEIL“, „FREITEXT“ und „MARKIERTER BEREICH“ ergänzen Hinweise. Rückgängig/Wiederholen sitzt über der Karte, Entf löscht das ausgewählte Element.",
    illustration: "draw",
  },
  {
    id: "ed-route-floors",
    chapter: "editor",
    roles: writers,
    title: "Laufwege & Ebenen",
    body: "Laufwege folgen der Karte samt Sprüngen, Drops und Leitern; Symbole zeigen, wo eine Aktion beginnt, gestrichelte Abschnitte liegen unter einer anderen Ebene. Liegen am ausgewählten Schritt Flächen übereinander, wechselst du mit Bild↑/Bild↓ oder den Pfeilen bei „EBENE“ die Ebene.",
    illustration: "route-floors",
  },
  {
    id: "ed-utility",
    chapter: "editor",
    roles: writers,
    title: "Utility platzieren",
    body: "Wähle eine Granate und klicke ihren Landepunkt – geworfen wird vom ausgewählten Schritt aus. Mit „AUSWÄHLEN“ verschiebst du Landepunkt und Wurfbogen per Drag.",
    illustration: "utility-place",
  },
  {
    id: "ed-library",
    chapter: "editor",
    roles: writers,
    title: "Gespeicherte Granaten",
    body: "Wähle zuerst einen Schritt, dann öffnet „GESPEICHERTE GRANATEN“ die Bibliothek aus dem Granatenkatalog. „ZU SCHRITT HINZUFÜGEN“ hängt das Lineup an genau diesen Schritt.",
    illustration: "library",
  },
  {
    id: "ed-inspector",
    chapter: "editor",
    roles: writers,
    title: "Inspector & Utility-Editor",
    body: "Rechts pflegst du unter „TAKTIK“ und „SPIELER“ Ziel, Rollen, Aufgaben und Schritte. Für eine ausgewählte Utility lädst du START-, AIM- und RESULT-Bilder hoch und schneidest sie im Medieneditor zu.",
    illustration: "inspector",
  },
  {
    id: "ed-utility-auto-verify",
    chapter: "editor",
    roles: writers,
    title: "Automatische Verifikation",
    body: "Eine Granate gilt als „Verifiziert“, sobald ihre Trajektorie berechnet ist, alle drei Bilder hochgeladen sind und eine Wurfart gewählt ist. Fährst du mit der Maus über die Status-Badge, siehst du, was noch fehlt.",
    illustration: "inspector",
  },
  {
    id: "ed-save",
    chapter: "editor",
    roles: writers,
    title: "Speichern & Vorschau",
    body: "„Speichern“ sichert deine Änderungen als Entwurf – auch eine freigegebene Taktik wird dadurch wieder zum Entwurf. „Vorschau“ zeigt die Taktik so, wie das Team sie in der Quickview sieht.",
    illustration: "save",
  },
  {
    id: "ed-snapshots",
    chapter: "editor",
    roles: ["editor"],
    title: "Veröffentlichungen",
    body: "Im Kontomenü zeigt „Veröffentlichungen“, welche Revision gerade live ist. Freigeben und Veröffentlichen übernimmt der Captain.",
    illustration: "snapshots",
  },
  {
    id: "ed-publish",
    chapter: "editor",
    roles: captain,
    title: "Freigeben & Veröffentlichen",
    body: "Setze den Status auf „Freigegeben“ und speichere, um eine Taktik freizugeben. „Veröffentlichen“ erstellt daraus eine neue Revision, die das ganze Team in der Quickview sieht.",
    illustration: "publish",
  },
  {
    id: "ed-rollback",
    chapter: "editor",
    roles: captain,
    title: "Rollback",
    body: "Unter „Veröffentlichungen“ siehst du alle Revisionen. „AKTIVIEREN“ macht eine ältere Revision sofort wieder zum aktiven Stand.",
    illustration: "rollback",
  },
  {
    id: "ed-eligibility",
    chapter: "editor",
    roles: captain,
    title: "Spielberechtigt",
    body: "Im Tab „SPIELER“ legst du unter „SPIELBERECHTIGT“ fest, welche Mitglieder einen Slot spielen dürfen. Daran orientiert sich die automatische Positionsverteilung im Live-Modus.",
    illustration: "eligibility",
  },
  {
    id: "ed-callouts",
    chapter: "editor",
    roles: captain,
    title: "Callout-Editor",
    body: "Über den Umschalter „Callouts“ pflegst du die Positionsnamen der Karte. Klicke zum Hinzufügen, ziehe zum Verschieben und doppelklicke zum Umbenennen.",
    illustration: "callouts",
  },

  // Granatenkatalog -------------------------------------------------------
  {
    id: "gc-browse",
    chapter: "catalog",
    roles: everyone,
    title: "Granaten finden",
    body: "Suche nach Namen oder filtere nach Granatentyp und wechsle zwischen „Liste“ und „Karte“. Auf der Karte zoomst du mit dem Mausrad.",
    illustration: "catalog-browse",
  },
  {
    id: "gc-modes",
    chapter: "catalog",
    roles: everyone,
    title: "Arbeiten & Ingame",
    body: "Im Modus „Arbeiten“ wählt ein Klick eine Granate nur aus – „DETAILS“ oder ein Doppelklick öffnet sie. „Ingame“ blendet alles außer Karte und Seite aus und zeigt Radar und Lineup-Bilder nebeneinander: Ein Klick aufs Icon genügt. Der Pfeil-Knopf klappt Titel und Navigationsleiste weg.",
    illustration: "catalog-modes",
  },
  {
    id: "gc-filters",
    chapter: "catalog",
    roles: everyone,
    title: "Filter kombinieren",
    body: "Links grenzt du nach „TAKTIK“, „BESITZ“, mehreren Granatentypen und „ZIELBEREICH“ ein. Die Zahlen zeigen, wie viele Granaten bei dieser Wahl übrig blieben.",
    illustration: "catalog-filters",
  },
  {
    id: "gc-details",
    chapter: "catalog",
    roles: everyone,
    title: "Details & Lineup-Bilder",
    body: "„DETAILS“ öffnet Start, Ziel und Wurftechnik der ausgewählten Granate. Die START-, AIM- und RESULT-Bilder zeigen dir das Lineup.",
    illustration: "catalog-details",
  },
  {
    id: "gc-create",
    chapter: "catalog",
    roles: writers,
    title: "Lineup anlegen",
    body: "„NEUE GRANATE“ öffnet den Editor: Setze Start und Ziel per Klick auf den Radar und prüfe den Wurf in der Physik-Preview. Bilder für START, AIM und RESULT machen das Lineup nachspielbar.",
    illustration: "catalog-create",
  },
  {
    id: "gc-share",
    chapter: "catalog",
    roles: writers,
    title: "Privat oder Team",
    body: "Neue Granaten können privat bleiben, bis sie sitzen. „TEILEN“ macht sie für das Team sichtbar, „LÖSCHEN“ entfernt sie nach Rückfrage.",
    illustration: "catalog-share",
  },
];

export const ONBOARDING_STEP_IDS: readonly string[] = ONBOARDING_STEPS.map(
  (step) => step.id,
);
